import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UploadApi } from './UploadApi';
import { useUploadStore } from './uploadStore';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UPLOAD_QUERY_KEYS } from '@/lib/constants/upload';
import { STORAGE_KEYS } from '@/lib/constants/storage';

/**
 * Hook: fetches ingest configs, handles permission restrictions,
 * auto-selects default, and persists selection to localStorage.
 *
 * PERMISSION RESTRICTION: If authData.cpuser.permissions.ingestcfgId is non-null,
 * user is restricted to that specific ingestcfg. Force-select it and return
 * isRestricted: true so IngestcfgSelector renders as disabled.
 */
export function useIngestcfgs(cId: number | null) {
  const { authData } = useAuth();
  const selectedIngestcfgId = useUploadStore((s) => s.selectedIngestcfgId);
  const setSelectedIngestcfgId = useUploadStore((s) => s.setSelectedIngestcfgId);

  const restrictedIngestcfgId = authData?.cpuser.permissions.ingestcfgId ?? null;

  const query = useQuery({
    queryKey: UPLOAD_QUERY_KEYS.ingestcfgs(cId!),
    queryFn: () => UploadApi.getIngestcfgs(cId!),
    enabled: cId != null,
    staleTime: 5 * 60 * 1000, // 5 min
  });

  // Sort by label ascending (matches legacy Ingest.store.ts:331)
  const sorted = useMemo(() => {
    if (!query.data) return [];
    return [...query.data].sort((a, b) => a.label.localeCompare(b.label));
  }, [query.data]);

  // Auto-select default on data load or cId change
  useEffect(() => {
    if (sorted.length === 0) return;

    // Permission restriction: force-select the restricted config
    if (restrictedIngestcfgId != null) {
      setSelectedIngestcfgId(restrictedIngestcfgId);
      return;
    }

    // Already selected and still valid
    if (selectedIngestcfgId != null && sorted.some((c) => c.ingestcfgId === selectedIngestcfgId)) {
      return;
    }

    // Try sessionStorage (tab-scoped)
    const stored = sessionStorage.getItem(STORAGE_KEYS.SELECTED_INGESTCFG);
    if (stored) {
      const storedId = Number(stored);
      if (sorted.some((c) => c.ingestcfgId === storedId)) {
        setSelectedIngestcfgId(storedId);
        return;
      }
    }

    // Fallback: first available
    setSelectedIngestcfgId(sorted[0].ingestcfgId);
  }, [sorted, restrictedIngestcfgId, selectedIngestcfgId, setSelectedIngestcfgId]);

  const handleSelect = (id: number) => {
    setSelectedIngestcfgId(id);
    sessionStorage.setItem(STORAGE_KEYS.SELECTED_INGESTCFG, String(id));
  };

  return {
    ingestcfgs: sorted,
    selectedIngestcfgId,
    setSelectedIngestcfgId: handleSelect,
    hasMultiple: sorted.length > 1,
    isRestricted: restrictedIngestcfgId != null,
    isLoading: query.isLoading,
  };
}
